import React, { useState } from 'react';

import { Button, Carousel, Descriptions, Flex, Form, Modal } from 'antd';
import Title from 'antd/es/typography/Title';
import { Dayjs } from 'dayjs';
import { t as globalT } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector, useStyle } from 'segments/hooks';
import { useNotification } from 'segments/hooks/useNotification';
import i18n from 'segments/i18n';

import { WeekPicker } from 'shared/FormItems';
import { ModalStats } from 'shared/Modals';

import { GroupAttendance } from 'features/adminOperations/components/GroupAttendance';
import { RescheduleBooking } from 'features/adminOperations/components/RescheduleBooking';
import { bookingType, changeActiveBookingDetail, changeActiveBookingEditParams, ConfirmBookingButton, selectActiveBookingIds, transformBkCourtToStats, useDeleteCourtBookingMutation, useGetBookingDetail, } from 'features/courtsBooking';
import { changeActiveModal } from 'features/modals';


interface Props {
  handleCancel: () => void;
}

type FormData = { deleteUntil?: Dayjs }

export const BookedCourtDetailModal = ({ handleCancel }: Props) => {
  const dispatch = useAppDispatch();
  const [showReschedule, setShowReschedule] = useState(false);
  const [hasRecordedAttendance, setHasRecordedAttendance] = useState(false);
  const { styles } = useStyle();
  const bookingIds = useAppSelector(selectActiveBookingIds);
  const { t } = useTranslation(undefined, { keyPrefix: 'booked-court.titles' });
  const [form] = Form.useForm<FormData>();
  const [deleteBooking, { isSuccess: isBookingDeleted, error }] = useDeleteCourtBookingMutation();
  const { courtBookingDetail, isBookingLoading } = useGetBookingDetail({ bookingIds });

  const onFinish = ({ deleteUntil }: FormData) => {
    if (bookingIds !== null) {
      bookingIds.forEach((bookingId) => {
        deleteBooking({
          bookingId,
          ...(deleteUntil && { deleteUntil: deleteUntil.format('YYYY-MM-DD') })
        });
      });
    }
  };

  useNotification({
    isSuccess: isBookingDeleted,
    onSuccess: () => handleCancel(),
    error
  });


  if (bookingIds === null || courtBookingDetail === undefined) {
    return;
  }

  const handleAddUser = () => {
    dispatch(changeActiveModal('add-user-to-booking'));
    dispatch(changeActiveBookingDetail({ bookingId: bookingIds[0], bookingType: bookingType[courtBookingDetail.type] }));
  };

  const handleEditBooking = () => {
    dispatch(changeActiveModal('edit-booked-court'));
    dispatch(changeActiveBookingEditParams({ bookingIds, note: courtBookingDetail.note, courtId: courtBookingDetail.courtId }));
  };

  const columnBookingType = courtBookingDetail.groupBookingId !== null ? 'groupe' : bookingType[courtBookingDetail.type];
  const isEditAble = !courtBookingDetail.confirmed || columnBookingType === 'groupe';
  const hasUser = courtBookingDetail.userIds.some((id) => id !== null);
  return (
    <Modal
      title={t('modal')}
      open
      onCancel={handleCancel}
      destroyOnClose
      className={styles.customScroolModal}
      loading={isBookingLoading && courtBookingDetail !== undefined}
      footer={null}
    >
      <ModalStats statsList={transformBkCourtToStats(courtBookingDetail)} statsTitles='booked-court.titles' />
      {
        columnBookingType !== 'groupe' && columnBookingType !== 'tournament' && (
          <Carousel arrows draggable infinite={false}>
            {
              courtBookingDetail.priceDetails.map((priceContent, elemIndex) => (
                <>
                  <Descriptions
                    column={2}
                    className={styles.customSmallDescHead}
                    title={`${t('prices')} ${courtBookingDetail.userNames[elemIndex] ?? ''}`}
                  >
                    <Descriptions.Item label={t('booking-price')}>{priceContent.courtTotal.toFixed(0)}₽</Descriptions.Item>
                    <Descriptions.Item label={t('coach-price')}>{priceContent.coachTotal.toFixed(0)}₽</Descriptions.Item>
                    <Descriptions.Item label={t('equipment-price')}>{priceContent.extraOptionsTotal.toFixed(0)}₽</Descriptions.Item>
                    <Descriptions.Item label={t('total-price')}>{courtBookingDetail.price[elemIndex].toFixed(0)}₽</Descriptions.Item>
                  </Descriptions>
                  {
                    priceContent.extraOptions.length > 0 && (
                      <Descriptions
                        className={styles.customSmallDescHead}
                        column={3} title={t('extra-options')}
                        style={{ marginTop: '4px' }}
                      >
                        {
                          priceContent.extraOptions.map(({ name, amount, price }) => (
                            <Descriptions.Item key={name} label={i18n.t(`products.items.${name}`)}>
                              {amount} x ₽{price}
                            </Descriptions.Item>
                          ))
                        }
                      </Descriptions>
                    )
                  }
                </>
              ))
            }
          </Carousel>
        )
      }
      {courtBookingDetail.groupBookingId !== null && (
        <GroupAttendance
          bookingId={courtBookingDetail.groupBookingId}
          bookingKind="court-bookings"
          onRecordedChange={setHasRecordedAttendance}
        />
      )}
      {showReschedule && bookingIds.length === 1 && (
        <RescheduleBooking
          bookingId={bookingIds[0]}
          currentStartTime={courtBookingDetail.startTime}
          currentCourtId={courtBookingDetail.courtId}
          onClose={() => setShowReschedule(false)}
          onMoved={handleCancel}
        />
      )}
      <Flex justify='space-between' style={{ marginTop: '6px' }}>
        {bookingIds.length === 1 && !hasRecordedAttendance && (
          <Button onClick={() => setShowReschedule(true)}>{globalT('admin-extensions.move-title')}</Button>
        )}
        {
          isEditAble && (
            <Button onClick={handleEditBooking}>{t('edit-booking')}</Button>
          )
        }
        {
          isEditAble && (hasUser || columnBookingType === 'groupe') && columnBookingType !== 'tournament' && (
            <Button onClick={handleAddUser} >{t('add-user')}</Button>
          )
        }
      </Flex>
      {
        hasUser && columnBookingType !== 'tournament' && (
          <div style={{ marginTop: '6px' }}>
            <ConfirmBookingButton
              isConfimed={courtBookingDetail.confirmed}
              bookingsId={bookingIds}
              handleCancel={handleCancel}
            />
          </div>
        )
      }
      {isEditAble && (
        <Form
          name="basic"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          className="edit-user__form"
          style={{ marginTop: '30px' }}
        >
          <Title level={5}>{t('delete')}</Title>
          <Flex justify='space-between' align='end'>
            <WeekPicker
              startWeek={courtBookingDetail.startTime}
              formItemsProps={{
                name: 'deleteUntil',
                label: t('delete-until')
              }}
            />
            <Button type="primary" danger htmlType="submit">{globalT('table.delete')}</Button>
          </Flex>
        </Form>
      )}
    </Modal >
  );
};
